/**
 * Lead form fields and their reader.
 *
 * The two live together on purpose: they must agree on every input `name`, and
 * splitting them across files is how that agreement quietly breaks. Both the
 * create and edit routes use this pair, so the two forms cannot drift apart.
 */

import type { Lead } from '@erp/contracts'
import { segment as segmentSchema } from '@erp/contracts'
import { choice, datetime, int, text } from '../forms'
import {
  optionsFrom,
  useIndustries,
  useLeadSources,
  useLeadStatuses,
  useOwnerOptions,
} from '../status-context'
import { Field, Select, TextInput } from '../ui'
import { LookupSelect } from './lookup-select'
import { toLocalInput } from '../format'

/**
 * Reads only the fields the form actually rendered.
 *
 * The distinction matters: a blank input means "clear this" (null), but an input
 * that was never rendered means "don't touch this" (absent). Reading everything
 * unconditionally would let the edit form silently clear the follow-up date
 * simply because it does not show that field. `form.has()` is the difference.
 *
 * It also means an edit form that renders all four identity fields gives
 * `updateLeadInput`'s refine enough to judge the identity rule client-side,
 * while a narrower patch is correctly left to the database.
 */
export function readLeadForm(form: FormData): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  const put = (key: string, value: unknown) => {
    if (form.has(key)) fields[key] = value
  }

  put('firstName', text(form, 'firstName'))
  put('lastName', text(form, 'lastName'))
  put('email', text(form, 'email'))
  put('phone', text(form, 'phone'))
  put('jobTitle', text(form, 'jobTitle'))
  put('organizationName', text(form, 'organizationName'))
  put('website', text(form, 'website'))
  put('industryId', text(form, 'industryId'))
  put('noOfEmployees', int(form, 'noOfEmployees'))
  put('sourceId', text(form, 'sourceId'))
  put('ownerId', text(form, 'ownerId'))
  put('segment', choice(form, 'segment', segmentSchema.options))
  put('nextFollowUpAt', datetime(form, 'nextFollowUpAt'))

  // statusId is optional rather than nullable — blank means "use the default",
  // not "clear it", so an empty select is omitted entirely.
  const statusId = text(form, 'statusId')
  if (statusId !== null) fields['statusId'] = statusId

  return fields
}

export function LeadFields({
  lead,
  issues,
  showPipeline = true,
}: {
  lead?: Lead | undefined
  issues: Record<string, string[]>
  /** Edit hides it: status and follow-up are managed from the detail side panel. */
  showPipeline?: boolean
}) {
  const statuses = useLeadStatuses()
  const sources = useLeadSources()
  const industries = useIndustries()
  const owners = useOwnerOptions(lead?.ownerId ?? null)

  const defaultStatus = lead?.statusId ?? statuses.find((s) => s.isDefault)?.id ?? statuses[0]?.id ?? ''

  return (
    <>
      <fieldset className="ax-fieldset">
        <legend>Person</legend>
        <Field label="First name" error={issues['firstName']}>
          <TextInput name="firstName" defaultValue={lead?.firstName} />
        </Field>
        <Field label="Last name" error={issues['lastName']}>
          <TextInput name="lastName" defaultValue={lead?.lastName} />
        </Field>
        <Field label="Email" error={issues['email']}>
          <TextInput name="email" type="email" defaultValue={lead?.email} />
        </Field>
        <Field label="Phone" error={issues['phone']}>
          <TextInput name="phone" type="tel" defaultValue={lead?.phone} />
        </Field>
        <Field label="Job title" error={issues['jobTitle']}>
          <TextInput name="jobTitle" defaultValue={lead?.jobTitle} />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Company</legend>
        {/* Free text until conversion creates a real organization record. */}
        <Field label="Organization" error={issues['organizationName']}>
          <TextInput name="organizationName" defaultValue={lead?.organizationName} />
        </Field>
        <Field label="Website" error={issues['website']}>
          <TextInput name="website" type="url" placeholder="https://" defaultValue={lead?.website} />
        </Field>
        <Field label="Industry" error={issues['industryId']}>
          <LookupSelect
            name="industryId"
            kind="industry"
            items={industries}
            defaultValue={lead?.industryId ?? null}
          />
        </Field>
        <Field label="Employees" error={issues['noOfEmployees']}>
          <TextInput
            name="noOfEmployees"
            type="number"
            defaultValue={lead?.noOfEmployees?.toString()}
          />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Pipeline</legend>
        {showPipeline ? (
          <Field label="Status" error={issues['statusId']}>
            <Select
              name="statusId"
              value={defaultStatus}
              options={statuses.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Field>
        ) : null}
        <Field label="Source" error={issues['sourceId']}>
          <LookupSelect
            name="sourceId"
            kind="lead_source"
            items={sources}
            defaultValue={lead?.sourceId ?? null}
          />
        </Field>
        <Field label="Owner" error={issues['ownerId']}>
          <Select name="ownerId" value={lead?.ownerId ?? ''} options={owners} />
        </Field>
        <Field label="Segment" error={issues['segment']}>
          <Select
            name="segment"
            value={lead?.segment ?? ''}
            options={[
              { value: '', label: 'Unspecified' },
              { value: 'b2b', label: 'B2B' },
              { value: 'b2c', label: 'B2C' },
              { value: 'd2c', label: 'D2C' },
            ]}
          />
        </Field>
        {showPipeline ? (
          <Field label="Next follow-up" error={issues['nextFollowUpAt']}>
            <TextInput
              name="nextFollowUpAt"
              type="datetime-local"
              defaultValue={lead?.nextFollowUpAt ? toLocalInput(lead.nextFollowUpAt) : ''}
            />
          </Field>
        ) : null}
      </fieldset>
    </>
  )
}
